import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import Alert, { AlertColor } from "@mui/material/Alert";
import { AccessControlContext } from "../contexts/AccessControlContext";
import { WorkflowEditUI } from "./WorkflowEditUI";
import { emptyWorkflowV1, emptyWorkflowV2, useWorkflowReducer } from "./workflowReducer";
import { Snackbar } from "../shared/Snackbar";
import { WorkflowV1 } from "../shared/interfaces/WorkflowV1";
import { useWorkflowCreate } from "../data/useWorkflowCreate";
import { WorkflowV2 } from "../shared/interfaces/WorkflowV2";
import config from "../../config";

interface Props {
  customerId: number;
  open?: boolean;
  onClose: () => void;
  onCreatedSuccess: () => void;
}

export function WorkflowCreate(props: Props) {
  const { t } = useTranslation();
  const formValidation = useForm<WorkflowV1>();

  const workflow = config.flexiSurveysEnabled ? (emptyWorkflowV2 as WorkflowV2) : (emptyWorkflowV1 as WorkflowV1);
  const newWorkflow = useWorkflowReducer(workflow);
  const updateWorkflowDetails = newWorkflow.updateWorkflowDetails;

  const workflowCreate = useWorkflowCreate({
    onSuccess: onCreateWorkflowSuccess,
    onError: onCreateWorkflowError,
  });

  const accessControlContext = useContext(AccessControlContext);
  const userCanCreateWorkflow = accessControlContext.canI("add", "workflow", props.customerId);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  useEffect(() => {
    updateWorkflowDetails({
      type: "updateWorkflowField",
      key: "customerId",
      value: props.customerId,
    });
  }, [updateWorkflowDetails, props.customerId, props.open]);

  function saveWorkflowUpdates() {
    if (userCanCreateWorkflow && newWorkflow && newWorkflow.state.workflow) {
      workflowCreate.mutate(newWorkflow.state.workflow);
    }
  }

  function onCreateWorkflowSuccess(workflowId: number) {
    setSnackbarText(
      t("common.dataSuccessfullyCreated", {
        entity: t("workflows.workflow"),
        id: workflowId,
      }),
    );
    setSnackbarSeverity("success");
    setShowSnackbar(true);
    updateWorkflowDetails({ type: "resetWorkflow" });
    props.onCreatedSuccess();
  }

  function onCreateWorkflowError() {
    setSnackbarText(t("common.dataCreateError", { val: "$t(workflows.workflow)" }));
    setSnackbarSeverity("error");
    setShowSnackbar(true);
  }

  const onSubmit = formValidation.handleSubmit(() => saveWorkflowUpdates());

  function handleCancelButtonClick() {
    newWorkflow.updateWorkflowDetails({ type: "resetWorkflow" });
    formValidation.clearErrors();
    formValidation.reset();
    props.onClose();
  }

  function handleSnackbarClose() {
    setShowSnackbar(false);
  }

  return (
    <>
      <FormProvider {...formValidation}>
        {!workflowCreate.isLoading && newWorkflow.state.workflow && (
          <WorkflowEditUI
            workflowReducer={newWorkflow}
            editMode
            isNewWorkflow
            onSave={onSubmit}
            saveLoading={workflowCreate.isLoading}
            onCancel={handleCancelButtonClick}
            isDialog
            dialogOpen={props.open}
          />
        )}

        <Snackbar open={showSnackbar} onClose={handleSnackbarClose}>
          <Alert severity={snackbarSeverity} variant="filled">
            {snackbarText}
          </Alert>
        </Snackbar>
      </FormProvider>
    </>
  );
}
